import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { clinicInfo } from '@/mocks/homeData';

export default function CTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="relative bg-background-100 py-24 md:py-32 lg:py-40 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,var(--primary-50),transparent_50%)] animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,var(--accent-50),transparent_50%)] animate-pulse" style={{ animationDuration: '8s', animationDelay: '3s' }} />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-[1000px] mx-auto">
          {/* Main CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative bg-background-50 rounded-2xl overflow-hidden border border-background-200/70"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Image with subtle float */}
              <motion.div
                animate={{ y: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
                className="relative h-64 lg:h-auto overflow-hidden"
              >
                <img
                  src="https://storage.readdy-site.link/project_files/ed85e06b-2b23-49e7-a477-ed0f88fc71a8/f38b7dd3-05f5-4485-933f-d45b2432c470_compressed_Captura-de-tela-2026-07-22-154501.webp"
                  alt="Agende sua consulta na Femme Vie"
                  className="w-full h-full object-cover object-top"
                />
              </motion.div>

              {/* Content */}
              <div className="p-8 md:p-12 lg:p-14 flex flex-col justify-center">
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">
                  Agendamento
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mt-3 leading-[1.1]">
                  Pronta para cuidar<br />
                  <span className="italic font-light">de você?</span>
                </h2>
                <p className="mt-5 text-sm text-foreground-500 leading-relaxed max-w-sm">
                  Agende sua consulta hoje mesmo e descubra uma nova forma de cuidar da sua saúde. Nossa equipe está pronta para acolher você.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={`https://wa.me/${clinicInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-primary-500 text-white text-sm tracking-[0.06em] uppercase whitespace-nowrap hover:bg-primary-600 transition-all duration-300 font-label"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    <span>WhatsApp</span>
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={`tel:${clinicInfo.phone.replace(/\D/g, '')}`}
                    className="group flex items-center justify-center gap-3 px-6 py-3.5 rounded-full border border-background-300 text-foreground-700 text-sm tracking-[0.06em] uppercase whitespace-nowrap hover:bg-background-100 transition-all duration-300 font-label"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    <span>{clinicInfo.phone}</span>
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="mt-8 rounded-xl overflow-hidden border border-background-200/70 h-[300px] md:h-[350px]"
          >
            <iframe
              src={clinicInfo.googleMapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização da Clínica Femme Vie"
            ></iframe>
          </motion.div>
        </div>
      </div>
    </section>
  );
}